import React, { useState, useEffect } from 'react';
import { DEBT_STATUS } from '../../constants';
import SelectorModal from '../SelectorModal';

const ExpenseSummary = ({ expenses, savedPayers, userProfile, onBack, onShowToast, onSaveDebts, appUser, friends = [], paymentMethods = [] }) => { // รับ appUser เพิ่ม (ถ้ามีส่งมา) หรือใช้ userProfile เช็คเบื้องต้น
  const [billAssignments, setBillAssignments] = useState({}); // { expenseId: 'name' }
  const [splitModes, setSplitModes] = useState({}); // { expenseId: 'half' | 'full' }
  const [summaryStep, setSummaryStep] = useState(1); // 1: Assign, 2: Result
  const [netResult, setNetResult] = useState(null);
  const [selectorModal, setSelectorModal] = useState(null); // { type: 'split' | 'payer', itemId: string }

  // Helper function to display payer name
  const getPayerDisplay = (expense) => {
    if (appUser && expense.payer_user_id === appUser.id) return 'ฉัน';
    if (expense.identifier === appUser?.line_user_id || expense.identifier === userProfile?.userId) return 'ฉัน';
    const friendBySystemId = expense.payer_user_id
      ? friends.find(f => f.id === expense.payer_user_id)
      : null;
    if (friendBySystemId) return friendBySystemId.display_name;
    const friendByLineId = expense.identifier
      ? friends.find(f => f.line_user_id === expense.identifier)
      : null;
    if (friendByLineId) return friendByLineId.display_name;
    if (!expense.identifier) return 'ไม่ระบุ';
    return expense.identifier.length > 20 ? 'User ' + expense.identifier.substring(0, 4) : expense.identifier;
  };

  const getPaymentMethodLabel = (expense) => {
    if (expense.payment_method_id != null) {
      const match = paymentMethods.find(pm => String(pm.id) === String(expense.payment_method_id));
      if (match?.name) return match.name;
    }
    return expense.payment_method || 'ไม่ระบุ';
  };

  // Sync state with expenses
  useEffect(() => {
    if (expenses && expenses.length > 0) {
        setSplitModes(prev => {
            const next = { ...prev };
            expenses.forEach(e => {
                if (next[e.id] === undefined) {
                    next[e.id] = e.split_mode || 'none'; // ใช้ค่าจาก DB ถ้ามี
                }
            });
            return next;
        });
    }
  }, [expenses]);

  // Sync bill assignments from DB
  useEffect(() => {
    if (expenses && expenses.length > 0) {
        setBillAssignments(prev => {
            const next = { ...prev };
            expenses.forEach(e => {
                if (next[e.id] === undefined && e.assigned_to) {
                    next[e.id] = e.assigned_to;
                }
            });
            return next;
        });
    }
  }, [expenses]);

  const calculateNetClearing = () => {
    const balances = {}; // name -> net value
    const details = {}; // name -> Array of details
    let totalPaid = 0;

    const initBalance = (name) => {
        if (balances[name] === undefined) balances[name] = 0;
        if (details[name] === undefined) details[name] = [];
    };

    const addDetail = (name, note, amount, type, related, calc, paymentMethod, expenseId) => {
        initBalance(name);
        details[name].push({ note, amount, type, related, calc, paymentMethod, expenseId });
    };

    // Identify participants
    const participants = new Set();
    participants.add('ฉัน');
    (expenses || []).forEach(e => participants.add(getPayerDisplay(e)));
    Object.values(billAssignments).forEach(name => {
        if (name) participants.add(name);
    });
    const participantList = Array.from(participants);

    // Calculate
    (expenses || []).forEach(item => {
        // Note: เรานำรายการที่ is_cleared มารวมคำนวณด้วย เพื่อให้สามารถแก้ไขและอัปเดตยอดใหม่ได้

        const payer = getPayerDisplay(item);
        const amount = parseFloat(item.amount) || 0;
        const mode = splitModes[item.id] || 'none';
        // ถ้าเลือก "ไม่เรียก" (none) ให้ถือว่าจ่ายให้ตัวเอง (ไม่เก็บใคร)
        const beneficiary = (mode === 'none') ? payer : (billAssignments[item.id] || payer);
        const note = item.note || 'ค่าใช้จ่าย';
        const paymentMethod = getPaymentMethodLabel(item);

        initBalance(payer);
        balances[payer] += amount; // Payer gains credit
        totalPaid += amount;

        // Specific beneficiary
        initBalance(beneficiary);
        
        if (payer !== beneficiary) {
            let debtAmount = 0;
            let calc = '';
            if (mode === 'none') {
                // Should not happen if logic is correct (payer == beneficiary), but safe fallback
                debtAmount = 0;
                balances[beneficiary] -= amount; // Payer pays and consumes
            } else if (mode === 'full') {
                // Full amount (borrowing / advance payment)
                debtAmount = amount;
                balances[beneficiary] -= amount; // Beneficiary consumes full amount
                calc = `${amount.toLocaleString()} (เต็มจำนวน)`;
            } else {
                // Half
                debtAmount = amount / 2;
                balances[payer] -= amount / 2;
                balances[beneficiary] -= amount / 2;
                calc = `${amount.toLocaleString()}/2 = ${debtAmount.toLocaleString()}`;
            }
            
            // Record details
            addDetail(payer, note, debtAmount, 'credit', beneficiary, calc, paymentMethod, item.id);
            addDetail(beneficiary, note, debtAmount, 'debit', payer, calc, paymentMethod, item.id);
        } else {
            // Payer == Beneficiary
            if (mode === 'half') { // Split with self? (Usually implies split with someone else, but if selected self...)
                balances[payer] -= amount; 
            } else {
                balances[beneficiary] -= amount;
            }
        }
    });

    const myNet = balances['ฉัน'] || 0;
    const myDetails = details['ฉัน'] || [];
    
    const others = Object.entries(balances)
        .filter(([name]) => name !== 'ฉัน' && Math.abs(balances[name]) > 0.01)
        .map(([name, net]) => ({ 
            name, 
            net,
            details: details[name] || []
        }))
        .sort((a, b) => a.net - b.net);

    setNetResult({ myNet, myDetails, others, totalPaid, participantCount: participantList.length });
    setSummaryStep(2);
  };

  const copySingleItem = (text) => {
    navigator.clipboard.writeText(text)
      .then(() => onShowToast('คัดลอกเรียบร้อย'))
      .catch(err => onShowToast('ไม่สามารถคัดลอกได้: ' + err, 'error'));
  };

  const generatePersonSummary = (name, net, details) => {
      let text = `👤 สรุปรายการของ ${name}\n`;
      text += `----------------\n`;
      
      if (details && details.length > 0) {
          // Group by payment method
          const grouped = details.reduce((acc, d) => {
              const method = d.paymentMethod || 'ไม่ระบุ';
              if (!acc[method]) acc[method] = [];
              acc[method].push(d);
              return acc;
          }, {});

          Object.entries(grouped).forEach(([method, items]) => {
              text += `💳 ${method}\n`;
              items.forEach(d => {
                  if (d.type === 'credit') {
                      text += `+ ${d.calc} : ${d.note} (จ่ายให้ ${d.related})\n`;
                  } else {
                      text += `- ${d.calc} : ${d.note} (จ่ายโดย ${d.related})\n`;
                  }
              });
              text += `\n`;
          });
      } else {
          text += `(ไม่มีรายการค้างชำระ)\n`;
      }
      text += `ยอดสุทธิ: ${net >= 0 ? `🟢 ได้คืน ${net.toLocaleString()}` : `🔴 ต้องจ่ายเพิ่ม ${Math.abs(net).toLocaleString()}`} บาท\n`;
      return text;
  };

  const totalAmount = (expenses || []).reduce((sum, item) => sum + (parseFloat(item.amount) || 0), 0);

  const isCalculateDisabled = (expenses || []).some(item => {
    const mode = splitModes[item.id] || 'none';
    return (mode === 'half' || mode === 'full') && !billAssignments[item.id];
  });

  return (
    <>
        <div style={{ marginBottom: '20px' }}>
        {summaryStep === 1 && (
            <button 
                onClick={onBack}
                style={{ background: 'none', border: 'none', color: 'var(--color-text-muted)', cursor: 'pointer', fontSize: '16px', display: 'flex', alignItems: 'center' }}
            >
                &larr; กลับหน้ารายการ
            </button>
        )}
        </div>
        <h2 style={{ color: 'var(--color-primary)', textAlign: 'center', marginBottom: '10px', fontSize: '24px', fontFamily: 'var(--font-display)' }}>
        {summaryStep === 1 ? 'สรุปรายจ่าย (1/2)' : 'ยอดจ่าย (2/2)'}
        </h2>

        <div style={{ backgroundColor: 'var(--color-glass-strong)', borderRadius: '16px', boxShadow: '0 18px 36px rgba(30, 70, 90, 0.12)', overflow: 'hidden', border: '1px solid var(--color-border)', padding: '20px' }}>
            {summaryStep === 1 && (
            <>
                {/* Total Card */}
                <div style={{ textAlign: 'center', marginBottom: '30px', padding: '20px', backgroundColor: 'var(--color-primary-soft)', borderRadius: '12px' }}>
                    <h3 style={{ margin: '0 0 10px 0', color: 'var(--color-primary)', fontSize: '16px' }}>ยอดรวมทั้งหมด</h3>
                    <p style={{ margin: 0, fontSize: '32px', fontWeight: 'bold', color: 'var(--color-primary)' }}>{totalAmount.toLocaleString()} THB</p>
                </div>

                {/* Detailed Bill List */}
                <h4 style={{ margin: '0 0 15px 0', color: 'var(--color-text-muted)' }}>รายการบิล (เลือกผู้รับผิดชอบ)</h4>
                <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                        <tr style={{ borderBottom: '2px solid var(--color-border)' }}>
                            <th style={{ textAlign: 'left', padding: '10px', color: 'var(--color-text-muted)', fontSize: '14px', whiteSpace: 'nowrap' }}>รายการ</th>
                            <th style={{ textAlign: 'right', padding: '10px', color: 'var(--color-text-muted)', fontSize: '14px', whiteSpace: 'nowrap' }}>จำนวน</th>
                            <th style={{ textAlign: 'center', padding: '10px', color: 'var(--color-text-muted)', fontSize: '14px', whiteSpace: 'nowrap' }}>เรียกเก็บ</th>
                            <th style={{ textAlign: 'right', padding: '10px', color: 'var(--color-text-muted)', fontSize: '14px', whiteSpace: 'nowrap' }}>เก็บที่ใคร</th>
                        </tr>
                    </thead>
                    <tbody>
                        {(expenses || []).map((item) => {
                            const payer = getPayerDisplay(item);
                            const assignment = billAssignments[item.id] || '';
                            const mode = splitModes[item.id] || 'none';
                            const isCleared = item.is_cleared;
                            const isPaid = item.debts && item.debts.status === DEBT_STATUS.PAID;
                            
                            return (
                                <tr key={item.id} style={{ borderBottom: '1px solid var(--color-surface-muted)' }}>
                                    <td style={{ padding: '12px 10px', minWidth: '150px' }}>
                                        <div style={{ fontWeight: '500', color: 'var(--color-text)' }}>{item.note || '-'}</div>
                                        <div style={{ fontSize: '12px', color: 'var(--color-text-subtle)', whiteSpace: 'nowrap' }}>จ่ายโดย: {payer}</div>
                                        {isCleared && (
                                            <span style={{ 
                                                fontSize: '10px', 
                                                backgroundColor: isPaid ? 'var(--color-primary-soft)' : 'var(--color-accent-soft)', 
                                                color: isPaid ? 'var(--color-primary)' : 'var(--color-accent)', 
                                                padding: '2px 6px', 
                                                borderRadius: '4px', 
                                                marginLeft: '5px' 
                                            }}>
                                                {isPaid ? 'เคลียร์แล้ว' : 'รอเคลียร์'}
                                            </span>
                                        )}
                                    </td>
                                    <td style={{ padding: '12px 10px', textAlign: 'right', fontWeight: 'bold', color: 'var(--color-primary)', whiteSpace: 'nowrap' }}>
                                        {item.amount.toLocaleString()}
                                    </td>
                                    <td style={{ padding: '12px 10px', textAlign: 'center', whiteSpace: 'nowrap' }}>
                                        <div 
                                            onClick={() => setSelectorModal({ type: 'split', itemId: item.id })}
                                            style={{ 
                                                padding: '10px', 
                                                borderRadius: '8px', 
                                                border: '1px solid var(--color-border)', 
                                                fontSize: '16px', 
                                                backgroundColor: 'var(--color-glass-strong)',
                                                minWidth: '90px',
                                                cursor: 'pointer',
                                                color: 'var(--color-text)'
                                            }}
                                        >
                                            {mode === 'half' ? 'หารเท่า' : (mode === 'full' ? 'เต็มจำนวน' : 'ไม่เรียก')}
                                        </div>
                                    </td>
                                    <td style={{ padding: '12px 10px', textAlign: 'right', whiteSpace: 'nowrap' }}>
                                        <div 
                                            onClick={() => mode !== 'none' && setSelectorModal({ type: 'payer', itemId: item.id })}
                                            style={{ 
                                                width: '100%',
                                                minWidth: '120px',
                                                padding: '10px', 
                                                borderRadius: '8px', 
                                                border: '1px solid var(--color-border)', 
                                                fontSize: '16px', 
                                                textAlign: 'left',
                                                backgroundColor: mode === 'none' ? 'var(--color-surface-muted)' : 'var(--color-glass-strong)',
                                                cursor: mode === 'none' ? 'default' : 'pointer',
                                                color: mode === 'none' ? 'var(--color-text-subtle)' : (assignment ? 'var(--color-text)' : 'var(--color-text-subtle)'),
                                                whiteSpace: 'nowrap',
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis'
                                            }}
                                        >
                                            {mode === 'none' ? '-' : (assignment || 'ระบุชื่อ...')}
                                        </div>
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
                </div>
                
                <div style={{ padding: '20px', textAlign: 'center' }}>
                    <button 
                        onClick={calculateNetClearing}
                        disabled={isCalculateDisabled}
                        style={{ 
                            padding: '12px 24px', 
                            backgroundColor: isCalculateDisabled ? 'var(--color-muted)' : 'var(--color-primary)', 
                            backgroundImage: isCalculateDisabled ? 'none' : 'var(--color-primary-gradient)',
                            color: 'white', 
                            border: 'none', 
                            borderRadius: '8px', 
                            fontSize: '16px', 
                            fontWeight: 'bold', 
                            cursor: isCalculateDisabled ? 'not-allowed' : 'pointer', 
                            boxShadow: isCalculateDisabled ? 'none' : '0 8px 18px rgba(var(--color-primary-rgb), 0.3)' 
                        }}
                    >
                        คำนวณยอด &rarr;
                    </button>
                </div>
            </>
            )}

            {summaryStep === 2 && netResult !== null && (
            <div style={{ padding: '10px' }}>
                <p style={{ textAlign: 'center', fontSize: '12px', color: 'var(--color-text-subtle)', marginBottom: '15px' }}>
                    (คำนวณจากผู้เกี่ยวข้องทั้งหมด {netResult.participantCount} คน)
                </p>
                
                <div style={{ backgroundColor: 'var(--color-accent-soft)', padding: '12px', borderRadius: '8px', marginBottom: '20px', border: '1px solid rgba(74, 164, 197, 0.35)' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: 'var(--color-accent)', fontWeight: 'bold', fontSize: '14px', marginBottom: '4px' }}>
                        <span style={{ fontSize: '16px' }}>💡</span> คำแนะนำ
                    </div>
                    <div style={{ fontSize: '13px', color: 'var(--color-text-muted)' }}>
                        คุณสามารถกดปุ่ม <strong>คัดลอก</strong> (รูปกระดาษ) หลังรายการของแต่ละคน เพื่อนำข้อความสรุปรายจ่ายไปแจ้งใน LINE/ LINE Group ได้ทันที
                    </div>
                </div>

                <div style={{ marginBottom: '15px', paddingBottom: '15px', borderBottom: '1px solid var(--color-border)' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '5px' }}>
                        <div>
                            <span style={{ color: 'var(--color-text-muted)' }}>สถานะของฉัน: </span>
                            <span style={{ fontWeight: 'bold', color: netResult.myNet >= 0 ? 'var(--color-primary)' : 'var(--color-danger)' }}>
                                {netResult.myNet >= 0 ? `ได้รับคืน ${netResult.myNet.toLocaleString()}` : `ต้องจ่ายเพิ่ม ${Math.abs(netResult.myNet).toLocaleString()}`} THB
                            </span>
                        </div>
                        <button 
                        onClick={() => copySingleItem(generatePersonSummary('ฉัน', netResult.myNet, netResult.myDetails))}
                        style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-text-subtle)', padding: '8px', display: 'flex', alignItems: 'center', transition: 'all 0.2s', borderRadius: '50%' }}
                        title="คัดลอก"
                        >
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
                            <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
                        </svg>
                        </button>
                    </div>
                </div>

                <div>
                    <h4 style={{ margin: '0 0 10px 0', fontSize: '14px', color: 'var(--color-text-muted)' }}>สถานะคนอื่น:</h4>
                    {netResult.others.length === 0 ? (
                        <p style={{ margin: 0, color: 'var(--color-text-subtle)', fontStyle: 'italic' }}>ไม่มีรายการ</p>
                    ) : (
                        netResult.others.map((item, index) => {
                            const totalText = item.net >= 0 
                                ? `ฉันต้องจ่าย ${item.name} ${item.net.toLocaleString()} บาท` 
                                : `${item.name} ต้องจ่ายฉัน ${Math.abs(item.net).toLocaleString()} บาท`;
                                
                            return (
                                <div key={index} style={{ display: 'flex', flexDirection: 'column', marginBottom: '12px', paddingBottom: '12px', borderBottom: '1px solid var(--color-border)', color: item.net >= 0 ? 'var(--color-primary)' : 'var(--color-danger)' }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                                        <span>{item.name}: </span>
                                        <span style={{ fontWeight: 'bold' }}>
                                            {item.net >= 0 ? `(ได้คืน) ${item.net.toLocaleString()}` : `(ต้องจ่าย) ${Math.abs(item.net).toLocaleString()}`}
                                        </span>
                                    </div>
                                    
                                    {/* Breakdown by Payment Method */}
                                    <div style={{ fontSize: '12px', color: 'var(--color-text-muted)', marginBottom: '8px', paddingLeft: '10px', borderLeft: '2px solid var(--color-border)' }}>
                                        {(() => {
                                            const groupedDetails = (item.details || []).reduce((acc, d) => {
                                                const method = d.paymentMethod || 'ไม่ระบุ';
                                                if (!acc[method]) acc[method] = 0;
                                                acc[method] += (d.type === 'credit' ? d.amount : -d.amount);
                                                return acc;
                                            }, {});
                                            
                                            return Object.entries(groupedDetails).map(([method, amount]) => (
                                                <div key={method} style={{ display: 'flex', justifyContent: 'space-between' }}>
                                                    <span>{method}</span>
                                                    <span style={{ color: amount >= 0 ? 'var(--color-primary)' : 'var(--color-danger)' }}>
                                                        {amount >= 0 ? `+${amount.toLocaleString()}` : amount.toLocaleString()}
                                                    </span>
                                                </div>
                                            ));
                                        })()}
                                    </div>

                                    <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
                                        <button 
                                            onClick={() => copySingleItem(totalText)}
                                            style={{ padding: '6px 12px', fontSize: '12px', cursor: 'pointer', borderRadius: '15px', border: '1px solid var(--color-border)', backgroundColor: 'var(--color-glass-strong)', color: 'var(--color-text-muted)', display: 'flex', alignItems: 'center', gap: '4px' }}
                                        >
                                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path></svg>
                                            ยอดรวม
                                        </button>
                                        <button 
                                            onClick={() => copySingleItem(generatePersonSummary(item.name, item.net, item.details))}
                                            style={{ padding: '6px 12px', fontSize: '12px', cursor: 'pointer', borderRadius: '15px', border: '1px solid var(--color-border)', backgroundColor: 'var(--color-glass-strong)', color: 'var(--color-text-muted)', display: 'flex', alignItems: 'center', gap: '4px' }}
                                        >
                                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path></svg>
                                            รายละเอียด
                                        </button>
                                    </div>
                                </div>
                            );
                        })
                    )}
                </div>

                <div style={{ marginTop: '30px', display: 'flex', gap: '10px', justifyContent: 'center' }}>
                    <button 
                        onClick={() => setSummaryStep(1)}
                        style={{ padding: '12px 24px', backgroundColor: 'var(--color-glass-strong)', color: 'var(--color-text-muted)', border: '1px solid var(--color-border)', borderRadius: '8px', fontSize: '16px', fontWeight: 'bold', cursor: 'pointer' }}
                    >
                        &larr; แก้ไขรายการ
                    </button>
                    <button 
                        onClick={() => onSaveDebts(netResult.others, { splitModes, billAssignments })}
                        style={{ padding: '12px 24px', backgroundColor: 'var(--color-primary)', backgroundImage: 'var(--color-primary-gradient)', color: 'white', border: 'none', borderRadius: '8px', fontSize: '16px', fontWeight: 'bold', cursor: 'pointer', boxShadow: '0 4px 6px rgba(var(--color-primary-rgb), 0.3)' }}
                    >
                        บันทึก
                    </button>
                </div>
            </div>
            )}
        </div>

        {selectorModal && (
            (() => {
                const item = (expenses || []).find(e => e.id === selectorModal.itemId);
                const payer = item ? getPayerDisplay(item) : '';
                // ถ้ายังไม่ได้เลือก (ค่าว่าง) ให้ถือว่าเก็บที่คนจ่าย (payer)
                const currentValue = selectorModal.type === 'split' 
                    ? (splitModes[selectorModal.itemId] || 'none') 
                    : (billAssignments[selectorModal.itemId] || payer);

                return <SelectorModal 
                type={selectorModal.type}
                initialValue={currentValue}
                options={selectorModal.type === 'payer' ? savedPayers : []}
                friends={friends}
                payerName={payer} // ส่งชื่อคนจ่ายเข้าไปเพื่อกรองตัวเลือก
                onSelect={(val) => {
                    if (selectorModal.type === 'split') {
                        setSplitModes(prev => ({ ...prev, [selectorModal.itemId]: val }));
                    } else {
                        setBillAssignments(prev => ({ ...prev, [selectorModal.itemId]: val }));
                    }
                    setSelectorModal(null);
                }}
                onClose={() => setSelectorModal(null)}
            />;
            })()
        )}
    </>
  );
};

export default ExpenseSummary;
